import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Spinner } from '../ui/Spinner';

type ProtectedRouteProps = {
  children: React.ReactNode;
  requireBusiness?: boolean;
  requiredType?: 'ADVERTISER' | 'PLACEMENT_PROVIDER';
  requireAdmin?: boolean;
  /** Requires the linked business entity to be verified. Unverified users are sent to /business/details. */
  requireVerifiedBusiness?: boolean;
  /** Pass true to block admin accounts from this route entirely. */
  noAdmin?: boolean;
};

export const ProtectedRoute = ({
  children,
  requireBusiness,
  requiredType,
  requireAdmin,
  requireVerifiedBusiness,
  noAdmin,
}: ProtectedRouteProps) => {
  const { isAuthenticated, businessEntityType, businessEntityVerified, isAdmin, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Admins have no business entity, so send them to /admin for any business-scoped route
  // or any route explicitly marked as off-limits to admins.
  if (isAdmin && (noAdmin || requireBusiness || requiredType)) {
    return <Navigate to="/admin" replace />;
  }

  // A just-created business entity is set optimistically in the store, but an in-flight
  // rehydrate() that started before that (e.g. the once-per-page-load one on app mount) can
  // still resolve afterwards with pre-creation data and null the reactive field out again -
  // bouncing the user straight back to /business/setup right after they created the entity,
  // making it look like nothing happened. localStorage is written synchronously by the same
  // call that sets the reactive field, and nothing clears it except an explicit logout, so
  // fall back to it here rather than trusting only the in-memory value.
  const hasKnownBusinessType = !!businessEntityType || !!localStorage.getItem('businessEntityType');
  if (requireBusiness && !hasKnownBusinessType) {
    return <Navigate to="/business/setup" replace />;
  }

  if (requiredType && businessEntityType !== requiredType) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireVerifiedBusiness && businessEntityVerified !== true) {
    return <Navigate to="/business/details" replace />;
  }

  return <>{children}</>;
};
